import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchInclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUninclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';
import { type BulkSpotlightTarget, type BulkSpotlightTransitionPlan } from '../utils/bulkSpotlightTransitions';

const INVALIDATION_META = {
  invalidates: ['getProperties', 'spotlightOverview'] as Array<string>,
};

type BulkSpotlightMutationResult = { listingAlias: string; success: boolean };

export const useSpotlightBatchMutations = () => {
  const { enqueueToast } = useToast();

  const { mutateAsync: batchExcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch exclude listings from spotlight' },
    },
  });

  const { mutateAsync: batchIncludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchInclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch include listings as smart assign candidates' },
    },
  });

  const { mutateAsync: batchPinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch pin listings in spotlight' },
    },
  });

  const { mutateAsync: batchUnexcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch unexclude listings from spotlight' },
    },
  });

  const { mutateAsync: batchUnincludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUninclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch uninclude listings as smart assign candidates' },
    },
  });

  const { mutateAsync: batchUnpinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch unpin listings from spotlight' },
    },
  });

  const executeBatch = async ({
    listingAliases,
    mutationFn,
    successMessage,
    errorMessage,
    toastId,
  }: {
    listingAliases: Array<ListingAlias>;
    mutationFn: (
      aliases: Array<ListingAlias>,
    ) => Promise<{ results: Array<{ listingAlias: ListingAlias; success: boolean }> }>;
    successMessage: (successCount: number, failCount: number) => string;
    errorMessage: string;
    toastId: string;
  }) => {
    try {
      const response = await mutationFn(listingAliases);
      const successCount = response.results.filter((r) => r.success).length;
      const failCount = response.results.length - successCount;
      enqueueToast(
        <Toast id={toastId} tone={failCount > 0 ? 'warning' : 'success'}>
          {successMessage(successCount, failCount)}
        </Toast>,
      );
    } catch (error) {
      enqueueToast(
        <Toast id={`${toastId}-error`} tone="critical">
          {errorMessage}
        </Toast>,
      );
      logError(error);
    }
  };

  const batchExclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchExcludeMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} excluded. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} excluded from Spotlight.`,
      errorMessage: 'Failed to exclude listings. Try again.',
      toastId: 'spotlight-batch-exclude',
    });

  // "Added to Smart Assign" is an opt-in, not a placement — the engine places it only when it ranks high enough.
  const batchInclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchIncludeMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} added to Smart Assign. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} added to Smart Assign.`,
      errorMessage: 'Failed to add listings to Smart Assign. Try again.',
      toastId: 'spotlight-batch-include',
    });

  const batchPin = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchPinMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} pinned. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} pinned to Spotlight.`,
      errorMessage: 'Failed to pin listings. Try again.',
      toastId: 'spotlight-batch-pin',
    });

  const batchUnexclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnexcludeMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} unexcluded. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} returned to auto-optimize.`,
      errorMessage: 'Failed to unexclude listings. Try again.',
      toastId: 'spotlight-batch-unexclude',
    });

  const batchUninclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnincludeMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} removed from Smart Assign. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} removed from Smart Assign.`,
      errorMessage: 'Failed to remove listings from Smart Assign. Try again.',
      toastId: 'spotlight-batch-uninclude',
    });

  const batchUnpin = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnpinMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} unpinned. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} returned to auto-optimize.`,
      errorMessage: 'Failed to unpin listings. Try again.',
      toastId: 'spotlight-batch-unpin',
    });

  const applyPlan = async (
    plan: BulkSpotlightTransitionPlan,
    target: BulkSpotlightTarget,
    smartConfigMode: SmartConfigMode,
    onSuccess?: (listingAliases: Array<string>) => void,
  ) => {
    const groups: Array<{
      aliases: Array<string>;
      mutationFn: (aliases: Array<string>) => Promise<{ results: Array<BulkSpotlightMutationResult> }>;
    }> = [
      {
        aliases: plan.aliasesByMutation.exclude,
        mutationFn: (aliases: Array<string>) => batchExcludeMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.include,
        mutationFn: (aliases: Array<string>) => batchIncludeMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.pin,
        mutationFn: (aliases: Array<string>) => batchPinMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.unexclude,
        mutationFn: (aliases: Array<string>) => batchUnexcludeMutation({ postData: { listingAliases: aliases } }),
      },
      {
        aliases: plan.aliasesByMutation.uninclude,
        mutationFn: (aliases: Array<string>) => batchUnincludeMutation({ postData: { listingAliases: aliases } }),
      },
    ].filter((group) => group.aliases.length > 0);

    const settled = await Promise.allSettled(groups.map((group) => group.mutationFn(group.aliases)));
    const results: Array<BulkSpotlightMutationResult> = [];
    settled.forEach((outcome, index) => {
      if (outcome.status === 'fulfilled') {
        results.push(...outcome.value.results);
        return;
      }
      logError(outcome.reason);
      results.push(...groups[index].aliases.map((listingAlias) => ({ listingAlias, success: false })));
    });

    const successCount = results.filter((result) => result.success).length;
    const failedCount = results.length - successCount;
    const successfulAliases = results.filter((result) => result.success).map((result) => result.listingAlias);
    if (successfulAliases.length > 0) {
      onSuccess?.(successfulAliases);
    }
    const targetLabel =
      target === 'PINNED'
        ? 'Pinned'
        : target === 'SMART_ASSIGN'
          ? 'Smart Assign'
          : smartConfigMode === SmartConfigMode.ALL_EXCLUDED
            ? 'Removed'
            : 'Excluded';
    const changedText = `${successCount} listing${successCount === 1 ? '' : 's'} moved to ${targetLabel}.`;
    const alreadyText = plan.alreadyAtTargetCount > 0 ? ` ${plan.alreadyAtTargetCount} already in that state.` : '';
    const failedText = failedCount > 0 ? ` ${failedCount} could not be updated.` : '';
    enqueueToast(
      <Toast id={`spotlight-batch-${target.toLowerCase()}`} tone={failedCount > 0 ? 'warning' : 'success'}>
        {changedText + alreadyText + failedText}
      </Toast>,
    );
    return failedCount === 0;
  };

  return { applyPlan, batchExclude, batchInclude, batchPin, batchUnexclude, batchUninclude, batchUnpin };
};
